import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { MessageWithSupportTicketId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import {
  createDraftsCollection as createDraftsCollectionCore,
  type DraftsCollection,
} from "@redotech/redo-support-v2/drafts/support-ticket-drafts-collection";
import {
  createSupportTicketListCollection as createSupportTicketListCollectionCore,
  type SupportTicketListCollectionWithLoader,
} from "@redotech/redo-support-v2/query/support-ticket-list-collection";
import type { SupportTicketQueryScope } from "@redotech/redo-support-v2/query/support-ticket-query-keys";

import {
  createMessagesCollection as createMessagesCollectionCore,
  createThreadsCollection as createThreadsCollectionCore,
  createTimelineEventsCollection as createTimelineEventsCollectionCore,
  fetchSupportTicketMessages as fetchSupportTicketMessagesCore,
  fetchSupportTicketThreads as fetchSupportTicketThreadsCore,
  supportTicketTranscriptQueryKeys,
  type MessagesCollection,
  type SupportTicketThreadCollectionItem,
  type ThreadsCollection,
  type TimelineEventsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
import {
  createPresenceCollection as createPresenceCollectionCore,
  type PresenceCollection,
} from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";

import {
  createOrdersCollection as createOrdersCollectionCore,
  createReturnsCollection as createReturnsCollectionCore,
  type OrdersCollection,
  type ReturnsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
import {
  createTagGroupsCollection as createTagGroupsCollectionCore,
  createTagsCollection as createTagsCollectionCore,
  type SupportTicketTagQueryScope,
  type TagGroupsCollection,
  type TagsCollection,
} from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
import {
  createSupportTicketUsersCollection as createSupportTicketUsersCollectionCore,
  type SupportTicketUsersCollection,
} from "@redotech/redo-support-v2/query/support-ticket-users-collection";
import type { QueryClient } from "@tanstack/react-query";
import {
  createMerchantSupportTicketDataSource,
  type MerchantSupportTicketDataSource,
} from "./support-ticket-data-source";

export const transcriptQueryKeys: {
  readonly all: readonly ["supportTickets", "transcript"];
  readonly threads: () => readonly ["supportTickets", "transcript", "threads"];
  readonly messages: () => readonly [
    "supportTickets",
    "transcript",
    "messages",
  ];
  readonly timelineEvents: () => readonly [
    "supportTickets",
    "transcript",
    "timelineEvents",
  ];
} = supportTicketTranscriptQueryKeys;

interface CreateGlobalCollectionsOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
  queryScope?: SupportTicketQueryScope;
  tagQueryScope?: SupportTicketTagQueryScope;
}

export async function fetchSupportTicketThreads(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<SupportTicketThreadCollectionItem[]> {
  return fetchSupportTicketThreadsCore(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    supportTicketId,
  );
}

export async function fetchSupportTicketMessages(
  effectApi: EffectApiContext,
  supportTicketId: SupportTicketId,
): Promise<MessageWithSupportTicketId[]> {
  return fetchSupportTicketMessagesCore(
    createMerchantSupportTicketDataSource({ effectApi }).transcript,
    supportTicketId,
  );
}

function createThreadsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createThreadsCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createMessagesCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createMessagesCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createTimelineEventsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createTimelineEventsCollectionCore({
    queryClient,
    api: dataSource.transcript,
    scope,
  });
}

function createPresenceCollection(queryClient: QueryClient) {
  return createPresenceCollectionCore({ queryClient });
}

function createTagsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketTagQueryScope,
) {
  return createTagsCollectionCore({ queryClient, api: dataSource.tags, scope });
}

function createTagGroupsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketTagQueryScope,
) {
  return createTagGroupsCollectionCore({
    queryClient,
    api: dataSource.tags,
    scope,
  });
}

function createSupportTicketUsersCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
) {
  return createSupportTicketUsersCollectionCore({
    queryClient,
    api: dataSource.users,
  });
}

function createOrdersCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createOrdersCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createReturnsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
  scope?: SupportTicketQueryScope,
) {
  return createReturnsCollectionCore({
    queryClient,
    api: dataSource.relatedRecords,
    scope,
  });
}

function createDraftsCollection(
  queryClient: QueryClient,
  dataSource: MerchantSupportTicketDataSource,
) {
  return createDraftsCollectionCore({ queryClient, api: dataSource.drafts });
}

export type SupportTicketListCollection = SupportTicketListCollectionWithLoader;

export interface GlobalSupportTicketCollections {
  supportTicketsList: SupportTicketListCollection;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  drafts: DraftsCollection;
  presence: PresenceCollection;
  tags: TagsCollection;
  tagGroups: TagGroupsCollection;
  users: SupportTicketUsersCollection;
  orders: OrdersCollection;
  returns: ReturnsCollection;
  timelineEvents: TimelineEventsCollection;
}

export function createGlobalSupportTicketCollections(
  options: CreateGlobalCollectionsOptions,
): GlobalSupportTicketCollections {
  const dataSource = createMerchantSupportTicketDataSource(options);
  const supportTicketsList = createSupportTicketListCollectionCore({
    queryClient: options.queryClient,
    api: dataSource.list,
    scope: options.queryScope,
    onUpdateError: (supportTicketId, err) => {
      console.error("[supportTickets] PATCH failed for", supportTicketId, err);
    },
  });
  const threads = createThreadsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const messages = createMessagesCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const drafts = createDraftsCollection(options.queryClient, dataSource);
  const presence = createPresenceCollection(options.queryClient);
  const tags = createTagsCollection(
    options.queryClient,
    dataSource,
    options.tagQueryScope,
  );
  const tagGroups = createTagGroupsCollection(
    options.queryClient,
    dataSource,
    options.tagQueryScope,
  );
  const users = createSupportTicketUsersCollection(
    options.queryClient,
    dataSource,
  );
  const orders = createOrdersCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const returns = createReturnsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  const timelineEvents = createTimelineEventsCollection(
    options.queryClient,
    dataSource,
    options.queryScope,
  );
  return {
    supportTicketsList,
    threads,
    messages,
    drafts,
    presence,
    tags,
    tagGroups,
    users,
    orders,
    returns,
    timelineEvents,
  };
}

export { type DraftCollectionItem } from "@redotech/redo-support-v2/drafts/support-ticket-draft-requests";
export { supportTicketDraftQueryKeys as draftQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-query-keys";
export {
  supportTicketOrderQueryKeys as orderQueryKeys,
  supportTicketReturnQueryKeys as returnQueryKeys,
  type OrderWithPickStatus,
} from "@redotech/redo-support-v2/query/support-ticket-related-records-collections";
export { supportTicketTagQueryKeys as tagQueryKeys } from "@redotech/redo-support-v2/query/support-ticket-tags-collections";
export {
  supportTicketPresenceQueryKeys as presenceQueryKeys,
  type SupportTicketPresenceRecord,
} from "@redotech/redo-support-v2/realtime/support-ticket-presence-collection";

export { supportTicketTranscriptQueryKeys as supportTicketTranscriptKeys } from "@redotech/redo-support-v2/query/support-ticket-transcript-collections";
